<script setup lang="ts">
import { computed } from 'vue'
import type { ProductOwnerActivityDay } from '../stores/product_owner_runs'
import {
  buildHeatmapWeeks,
  heatmapCellColorClass,
  heatmapCellTitle,
  HEATMAP_LEVELS,
  HEATMAP_NO_DATA_LEVEL,
} from '../composables/productOwnerActivityHeatmap'

// GitHub-contribution-style heatmap for the Activity tab (#1036): one
// column per week, one small card per day, colored by that day's idle
// percentage across every opted-in repo. The date-math lives in
// productOwnerActivityHeatmap.ts so it's unit-testable without mounting
// this component.
const props = defineProps<{
  days: ProductOwnerActivityDay[]
}>()

const weeks = computed(() => buildHeatmapWeeks(props.days))

// Derived from the same HEATMAP_LEVELS the color function uses, reversed to
// read low-idle-first — so a legend color/label never drifts from the cells
// it's meant to explain.
const legend = [
  { label: HEATMAP_NO_DATA_LEVEL.label, class: HEATMAP_NO_DATA_LEVEL.colorClass },
  ...HEATMAP_LEVELS.toReversed().map((level) => ({ label: level.label, class: level.colorClass })),
]
</script>

<template>
  <div class="flex flex-col gap-3" data-testid="product-owner-activity-heatmap">
    <div class="flex gap-[3px] overflow-x-auto pb-1">
      <div v-for="(week, wi) in weeks" :key="wi" class="flex flex-col gap-[3px]">
        <div
          v-for="(cell, di) in week.cells"
          :key="di"
          class="w-3 h-3 rounded-sm"
          :class="heatmapCellColorClass(cell)"
          :title="heatmapCellTitle(cell)"
          :data-testid="cell ? `heatmap-cell-${cell.date}` : undefined"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/50">
      <span v-for="item in legend" :key="item.label" class="flex items-center gap-1.5">
        <span class="w-3 h-3 rounded-sm inline-block" :class="item.class" />
        {{ item.label }}
      </span>
    </div>
  </div>
</template>
